type IdleCallback = () => void;
type IdleOptions = { timeout?: number };

/**
 * Schedule a callback to run during browser idle time.
 *
 * Use this to defer non-critical work (analytics events, storage writes, prefetches) off
 * the app-start critical path. Work scheduled here will not contribute to the
 * `checkout_app_load_time` metric, which is captured inside `useEffectOnce` of CheckoutMain.
 *
 * Behaviour:
 * - Uses `window.requestIdleCallback` when available (Chrome / Android WebView).
 * - Falls back to `setTimeout(cb, 0)` on runtimes without rIC (notably iOS Safari).
 *   A `requestidlecallback` polyfill ships in `Bootstrap.ts`, so in practice the rIC path
 *   is taken everywhere; the fallback here keeps this helper resilient if the polyfill is
 *   ever removed.
 * - No-op during SSR.
 *
 * Not cancellable by design — callers are expected to be idempotent side-effects (flushing
 * storage, sending events). If you need to guarantee the callback runs even when the page
 * is unloading, register your own `pagehide` listener alongside the scheduleIdle call.
 *
 * @param cb callback to run
 * @param opts.timeout deadline after which the browser must run the callback even under CPU
 *                     pressure (default 2000ms)
 */
export const scheduleIdle = (cb: IdleCallback, opts: IdleOptions = {}): void => {
    if (typeof window === "undefined") return;
    const { timeout = 2000 } = opts;
    const ric = (window as Window & typeof globalThis).requestIdleCallback;
    if (typeof ric === "function") {
        ric(cb, { timeout });
        return;
    }
    setTimeout(cb, 0);
};
